/**
 * Microsoft Entra's privileged directory roles: the one list every consumer reads (KRAK-470).
 *
 * `stolos/docs/account-risk-spec.md` §3.1 owns it and is authoritative over this file. Global Administrator is alone
 * in `global`; every other role listed is `privileged`; everything else (including read-only roles) is `none`. The
 * Stolos risk catalogue (`stolos/src/risk/catalogue.ts`) re-exports it, and its ratification test checks each id
 * against a dynalite fixture's `ROLE#<id>` items, so a typo fails a test rather than silently demoting a role. The
 * event classifier (`shared/microsoft-events/src/classifier`) reads the same list to decide whether the role an
 * application granted is privileged.
 *
 * Constants only, no imports: the classifier is also compiled into the legacy browser frontend, which has no
 * `@octiga/*` alias for this package, so it takes the file by relative path.
 */

/** One row of §3.1's privileged-role table: DATA, the definition. */
export interface PrivilegedRole {
    /** The tier the role confers. */
    tier: 'global' | 'privileged';
    /** Display name, for the ratification snapshot and evidence. */
    name: string;
    /** Microsoft's well-known role-template id. */
    templateId: string;
}

/**
 * §3.1's privileged-role table. See the module doc for the tiers and the ratification test that pins the ids.
 */
export const PRIVILEGED_ROLES: readonly PrivilegedRole[] = [
    { tier: 'global', name: 'Global Administrator', templateId: '62e90394-69f5-4237-9190-012177145e10' },
    { tier: 'privileged', name: 'Privileged Role Administrator', templateId: 'e8611ab8-c189-46e8-94e1-60213ab1f814' },
    {
        tier: 'privileged',
        name: 'Privileged Authentication Administrator',
        templateId: '7be44c8a-adaf-4e2a-84d6-ab2649e08a13',
    },
    { tier: 'privileged', name: 'Security Administrator', templateId: '194ae4cb-b126-40b2-bd5b-6091b380977d' },
    { tier: 'privileged', name: 'Exchange Administrator', templateId: '29232cdf-9323-42fd-ade2-1d097af3e4de' },
    { tier: 'privileged', name: 'SharePoint Administrator', templateId: 'f28a1f50-f6e7-4571-818b-6a12f2af6b6c' },
    { tier: 'privileged', name: 'User Administrator', templateId: 'fe930be7-5e62-47db-91af-98c3a49a38b1' },
    {
        tier: 'privileged',
        name: 'Conditional Access Administrator',
        templateId: 'b1be1c3e-b65d-4f19-8427-f6fa0d97feb9',
    },
    { tier: 'privileged', name: 'Application Administrator', templateId: '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3' },
    {
        tier: 'privileged',
        name: 'Cloud Application Administrator',
        templateId: '158c047a-c907-4556-b7ef-446551a6b5f7',
    },
    { tier: 'privileged', name: 'Authentication Administrator', templateId: 'c4e39bd9-1100-46d3-8c65-fb160da0071f' },
    { tier: 'privileged', name: 'Intune Administrator', templateId: '3a2c62db-5318-420d-8d74-23affee5d9d5' },
    { tier: 'privileged', name: 'Hybrid Identity Administrator', templateId: '8ac3fc64-6eca-42ea-9e69-59f4c7b60eb2' },
] as const;
